import { Box, Tooltip } from '@mui/material';
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { MainMenuItem } from '..';
import { MiniDrawerMainProps } from './MiniDrawer.props';
import { getIconForSection } from '../DrawerContent/iconMap';
import { findActiveUrl, sectionContainsActive } from '../navActive';

const iconWrapperSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  py: '8px',
  borderLeft: '3px solid transparent',
  cursor: 'pointer',
  color: '#cbd5e1',
  textDecoration: 'none',
  transition: 'background .15s',
  '&:hover': { bgcolor: '#0f172a', color: '#fff' },
} as const;

const activeSx = {
  bgcolor: 'v4.sidebarActive',
  borderLeftColor: 'v4.accent',
  color: '#fff',
} as const;

const iconSize = { fontSize: 20 } as const;

export const MiniDrawer: React.FC<MiniDrawerMainProps> = ({ menuData }) => {
  const location = useLocation();
  // See DrawerContent's identical resolution — a detail route not itself in
  // the menu still resolves to its owning list page's url.
  const activeUrl = useMemo(
    () => findActiveUrl(menuData, location.pathname),
    [menuData, location.pathname]
  );

  const getFirstUrl = (item: MainMenuItem): string => {
    if (item.url) return item.url;
    if (item.subMenuItems?.[0]) {
      const sub = item.subMenuItems[0];
      if (sub.url) return sub.url;
      if (sub.subMenuItems?.[0]?.url) return sub.subMenuItems[0].url;
    }
    return '';
  };

  return (
    <Box component="nav" aria-label="Mini navigation">
      {menuData.map((item) => {
        const Icon = getIconForSection(item.name);
        const active = sectionContainsActive(activeUrl, item);
        const url = getFirstUrl(item);

        return (
          <Tooltip key={item.id} title={item.name} placement="right" arrow>
            {url ? (
              <Box
                component={Link}
                to={url}
                sx={{ ...iconWrapperSx, ...(active ? activeSx : {}) }}
                aria-label={item.name}
                aria-current={active ? 'page' : undefined}
              >
                <Icon sx={iconSize} />
              </Box>
            ) : (
              <Box sx={{ ...iconWrapperSx, ...(active ? activeSx : {}) }} aria-label={item.name}>
                <Icon sx={iconSize} />
              </Box>
            )}
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default MiniDrawer;
